import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, Observer, of, ReplaySubject } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService } from 'app/auth/service';
import { User } from 'app/auth/models';
import { CognitoUser } from '@aws-amplify/auth';
import { CognitoUserSession, ISignUpResult } from 'amazon-cognito-identity-js';
import { ConfigService } from 'app/app-config.service';
import { Router } from '@angular/router';
import { Auth, Amplify, Hub } from 'aws-amplify';
import { FederatedSignInOptions, CognitoHostedUIIdentityProvider } from '@aws-amplify/auth/lib-esm/types/Auth';
import { LocalStorageKeys, StorageService } from 'app/main/services/storage/storage.service';
import { Store } from '@ngxs/store';
import { UserAction } from 'app/main/state/user.state';
import { UserHttpService } from '@services/datasources/user-http.service';

@Injectable({ providedIn: 'root' })
export class CognitoAuthenticationService implements AuthenticationService {
    //public
    public currentUser$: Observable<User | null> = of(null);
    public authenticatedUser!: CognitoUser;
    public authenticatedUser$ = new ReplaySubject<CognitoUser>(1);

    //private
    private currentUserSubject: BehaviorSubject<User | null>;
    private userToValidate: { username: string; password: string } | undefined;
    client = this.configService.getClient;
    private clientName: string = this.client.name;

    private newPasswordRequiredUser: CognitoUser | null;

    constructor(
        private configService: ConfigService,
        private router: Router,
        private localStorageService: StorageService,
        private store: Store,
        private userService: UserHttpService,
    ) {
        Amplify.configure({
            Auth: {
                // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
                identityPoolId: '',

                // REQUIRED - Amazon Cognito Region
                region: this.configService.getConfig.cognitoRegion, //'eu-central-1',

                // OPTIONAL - Amazon Cognito User Pool ID
                userPoolId: this.configService.getConfig.cognitoUserPoolId,

                // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
                userPoolWebClientId: this.configService.getConfig.cognitoAppClientId,

                storage: localStorage,

                // OPTIONAL - Manually set the authentication flow type. Default is 'USER_SRP_AUTH'
                // authenticationFlowType: 'USER_PASSWORD_AUTH',

                // OPTIONAL - Hosted UI configuration
                oauth: {
                    domain: this.configService.getConfig.oauth.domain,
                    scope: this.configService.getConfig.oauth.scope,
                    redirectSignIn: this.configService.getConfig.oauth.redirectSignIn,
                    redirectSignOut: this.configService.getConfig.oauth.redirectSignOut,
                    responseType: 'code', // or 'token', note that REFRESH token will only be generated when the responseType is code
                },
            },
        });

        Hub.listen('auth', ({ payload: { event, data } }) => {
            if (event === 'signIn') {
                const cognitoUser: CognitoUser = data;
                //Signin Hub listener only for federated login
                if (cognitoUser.getSignInUserSession()?.getIdToken()) {
                    this._handleCognitoSession(cognitoUser);
                }
            }
            return;
        });

        const json = localStorageService.getGlobalItem<any>(LocalStorageKeys.currentUser);
        this.currentUserSubject = new BehaviorSubject<User | null>(json ? JSON.parse(json) : null);

        this.currentUser$ = this.currentUserSubject.asObservable();

        this.authenticatedUser$.subscribe((user) => (this.authenticatedUser = user));

        this.currentUserSubject.subscribe((user) => {
            if (user) {
                this.currentUser$ = this.currentUserSubject.asObservable();
            }
        });

        //Get currently authenticated user if any
        Auth.currentAuthenticatedUser()
            .then((cognitouser) => {
                if (cognitouser) this.authenticatedUser$.next(cognitouser);
            })
            .catch((error) => {});

        this.newPasswordRequiredUser = null;
    }

    public isNewPasswordRequired(): boolean {
        return this.newPasswordRequiredUser != null;
    }

    // getter: currentUserValue
    public get currentUserValue(): User | null {
        return this.currentUserSubject.value;
    }

    public set currentUSerValue(user: User | null) {
        this.currentUserSubject.next(user);
    }

    /**
     *  Federated login via Google or Facebook
     */
    public federatedLogin(providerType: string): Observable<any> {
        const provider: FederatedSignInOptions = {
            provider: CognitoHostedUIIdentityProvider.Google,
        };
        if (providerType == CognitoHostedUIIdentityProvider.Facebook) {
            provider.provider = CognitoHostedUIIdentityProvider.Facebook;
        }

        return from(Auth.federatedSignIn(provider));
    }

    /**
     * Check if the current user is authenticated
     * JWT token is refreshed during getSession
     */
    public isLoggedIn(refreshStorage?: boolean): Observable<boolean> {
        if (refreshStorage === undefined) refreshStorage = true;
        let isAuth = false;
        return new Observable((observer: Observer<boolean>) => {
            // Handle refresh of new user jwt token
            Auth.currentSession().then(
                (cognitoUserSession) => {
                    isAuth = cognitoUserSession.isValid();
                    if (refreshStorage) this._handleCognitoSession(undefined, cognitoUserSession);
                    observer.next(isAuth && (this.currentUserValue != null || this.currentUserValue != undefined));
                    observer.complete();
                },
                (err) => {
                    observer.next(false);
                    observer.complete();
                    // observer.error(err.message || JSON.stringify(err));
                },
            );
        });
    }

    /**
     * User (described by its email) has forgotten its password
     * Request a verification to be able to reset it
     */
    public forgotPassword(email: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            Auth.forgotPassword(email).then(
                (result) => {
                    const destination = result['CodeDeliveryDetails']['Destination'];
                    observer.next(destination);
                    observer.complete();
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    /**
     * Confirm username registration with a verification code (email or sms). Email in our case
     */
    public confirmRegistration(username: string, verificationCode: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            Auth.confirmSignUp(username, verificationCode).then(
                () => {
                    observer.next('ok');
                    observer.complete();
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    /**
     * Reset password based on a verification code provided by the idp
     * for a specific username with a new password (newPassword)
     */
    public resetPassword(username: string, verificationCode: string, newPassword: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            Auth.forgotPasswordSubmit(username, verificationCode, newPassword).then(
                (result) => {
                    observer.next(result);
                    observer.complete();
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    public acceptTerms(value: number): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            Auth.updateUserAttributes(this.authenticatedUser, { 'custom:has_accepted_terms': value.toString() }).then(
                (res) => {
                    // get new claims and store it
                    if (this.currentUserValue !== null) {
                        const newUser: User = { ...this.currentUserValue, has_accepted_terms: '1' };
                        this.currentUserSubject.next(newUser);
                    }
                    observer.next(res);
                    observer.complete();
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    public updateUserLocale(locale: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            Auth.updateUserAttributes(this.authenticatedUser, { locale: locale }).then(
                (res) => {
                    this.localStorageService.setGlobalItem(LocalStorageKeys.localeUser, locale);
                    observer.next(res);
                    observer.complete();
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    private updateAttributes(
        firstName: string,
        lastName: string,
        phone_number: string,
        role: string,
    ): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            Auth.updateUserAttributes(this.authenticatedUser, {
                phone_number: phone_number,
                given_name: firstName,
                family_name: lastName,
                'custom:role': role,
            }).then(
                (result) => {
                    // Handle refresh of new user jwt token
                    Auth.currentSession().then(
                        (cognitoUserSession) => {
                            this._handleCognitoSession(undefined, cognitoUserSession);
                            observer.next(result);
                            observer.complete();
                        },
                        (err) => {
                            observer.error(err);
                        },
                    );
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    /**
     * Update user Attributes
     */
    public updateUserAttributes(
        firstName: string,
        lastName: string,
        phoneNumber: string,
        role: string,
    ): Observable<string> {
        return this.updateAttributes(firstName, lastName, phoneNumber, role).pipe(
            map((result) => {
                return result;
            }),
        );
    }

    private cognitoSignup(
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        userLanguage: string,
        companyName: string,
    ): Observable<ISignUpResult | undefined> {
        return new Observable((observer: Observer<ISignUpResult | undefined>) => {
            // If user language is undefined, set default language
            const hasLanguage = !!userLanguage;
            if (!hasLanguage) userLanguage = 'en';
            Auth.signUp({
                username: email,
                password: password,
                attributes: {
                    email: email,
                    locale: userLanguage,
                    given_name: firstName,
                    family_name: lastName,
                    phone_number: '+3200000000000',
                    'custom:company_name': companyName,
                    'custom:is_initialized': '0',
                    'custom:has_accepted_terms': '1',
                },
            }).then(
                (result) => {
                    observer.next(result);
                    observer.complete();
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    /**
     * User sign up
     */
    public signUp(
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        userLanguage: string,
        companyName: string,
    ): Observable<string> {
        return this.cognitoSignup(email, password, firstName, lastName, userLanguage, companyName).pipe(
            map((result) => {
                if (result != undefined) {
                    const cognitoUser = result.user;
                    this._handleCognitoSession(cognitoUser);
                    return cognitoUser.getUsername();
                } else {
                    return '';
                }
            }),
        );
    }

    /**
     * Resend confirmation code for a specific username (email)
     */
    public resendCode(email: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            Auth.resendSignUp(email).then(
                (result) => {
                    observer.next(result);
                    observer.complete();
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    private handleNewPassword(newPassword: string): Observable<CognitoUser | null> {
        return new Observable((observer: Observer<CognitoUser>) => {
            Auth.completeNewPassword(this.newPasswordRequiredUser, newPassword, {}).then(
                (user) => {
                    observer.next(user);
                    observer.complete();
                    this.newPasswordRequiredUser = null;
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    /**
     * When the user is created by the admin
     * The user must change is password on first login
     * After the new password is handle, the user is logged in.
     * Store JWT in local storage
     * Handle Cognito session
     */
    public newPasswordRequired(newPassword: string): Observable<User | undefined> {
        return this.handleNewPassword(newPassword).pipe(
            mergeMap((cognitoUser: CognitoUser | null) => {
                if (cognitoUser) return this._handleCognitoSession(cognitoUser);
                return of(undefined);
            }),
        );
    }

    private authenticate(email: string, password: string): Observable<CognitoUser | Array<any>> {
        return new Observable((observer: Observer<CognitoUser | Array<any>>) => {
            Auth.signIn(email, password).then(
                (data: CognitoUser | any) => {
                    if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
                        this.newPasswordRequiredUser = data;
                        observer.next(['newPasswordRequired', data]);
                        observer.complete();
                    } else {
                        const cognitoUser = data;
                        if (cognitoUser) {
                            observer.next(cognitoUser);
                            observer.complete();
                        } else {
                            observer.error('Sign In error');
                        }
                    }
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    private _storeCognitoUserSession(cognito_user_session: CognitoUserSession): User {
        const current_user = new User();
        // login successful if there's a jwt token in the response
        const idToken = cognito_user_session.getIdToken();

        current_user.email = idToken.payload['email'];

        if ('locale' in idToken.payload) {
            current_user.locale = idToken.payload['locale'];
        }
        if ('given_name' in idToken.payload) {
            current_user.firstName = idToken.payload['given_name'];
        }
        if ('family_name' in idToken.payload) {
            current_user.lastName = idToken.payload['family_name'];
        }
        if ('custom:role' in idToken.payload) {
            current_user.role = idToken.payload['custom:role'];
        }
        if ('custom:company_name' in idToken.payload) current_user.companyName = idToken.payload['custom:company_name'];
        // THIS IS TO ENSURE ALREADY REGISTERED USER ARE CONSIDERED AS INITIALIZED. THIS FIELD WOULD ALWAYS BE FILLED OUT IN THE REGISTER CASE
        current_user.isInitialized = idToken.payload['custom:is_initialized'] ?? '1';
        current_user.has_accepted_terms = idToken.payload['custom:has_accepted_terms'] ?? '0';

        current_user.token = idToken.getJwtToken();

        // store user details and jwt token in local storage to keep user logged in between page refreshes
        this.localStorageService.setGlobalItem(LocalStorageKeys.currentUser, JSON.stringify(current_user));
        this.localStorageService.setGlobalItem<string>(
            LocalStorageKeys.localeUser,
            current_user.locale ?? this.configService.getConfig.defaultLocale,
        );

        return current_user;
    }

    /**
     * User login
     * Store JWT in local storage
     * Handle Cognito session
     */
    public login(email: string, password: string): Observable<User | string> {
        return this.authenticate(email, password).pipe(
            mergeMap((data) => {
                if (data[0] === 'newPasswordRequired') {
                    this.newPasswordRequiredUser = data[1];
                    return of(data[0]);
                }
                if (data instanceof CognitoUser) return this._handleCognitoSession(data);
                throw 'unexpected';
            }),
        );
    }

    private _handleCognitoSession(
        cognitoUser?: CognitoUser,
        cUserSession?: CognitoUserSession,
    ): Observable<User | undefined> {
        const cognitoUserSession = cUserSession ? cUserSession : cognitoUser!.getSignInUserSession();
        if (cognitoUser) this.authenticatedUser$.next(cognitoUser);

        if (
            cognitoUserSession &&
            cognitoUserSession.getIdToken().getJwtToken() != this.currentUserSubject.value?.token
        ) {
            const current_user = this._storeCognitoUserSession(cognitoUserSession);
            if (current_user.isInitialized === '0') {
                return this.userService
                    .initUser(
                        {
                            name: current_user.companyName!,
                            firstName: current_user.firstName,
                            lastName: current_user.lastName,
                            email: current_user.email,
                            locale: current_user.locale,
                        },
                        this.clientName,
                    )
                    .pipe(
                        catchError(() => {
                            throw 'InitFailed';
                        }),
                        switchMap((r) => {
                            return new Observable((observer) => {
                                Auth.updateUserAttributes(this.authenticatedUser, {
                                    'custom:is_initialized': '1',
                                })
                                    .then(() => {
                                        current_user.isInitialized = '1';
                                        observer.next(r);
                                        observer.complete();
                                    })
                                    .catch((err) => observer.error(err));
                            });
                        }),
                        tap(() => this.currentUserSubject.next(current_user)),
                        map(() => current_user),
                    );
            } else {
                // notify
                this.currentUserSubject.next(current_user);

                return of(current_user);
            }
        }

        return of(this.currentUserSubject.value ?? undefined);
    }

    /**
     * User logout
     */
    public logout(redirect?: boolean) {
        if (redirect === undefined) redirect = true;
        // remove user from local storage to log user out
        this.localStorageService.removeItem(LocalStorageKeys.currentUser);
        this.store.dispatch(new UserAction.Deactivate());
        Chathive.widget.updateCustomerData({});
        this.currentUserSubject.next(null);

        return new Observable<void>((observer) => {
            Auth.signOut().then(
                () => {
                    if (!redirect) {
                        observer.next();
                        observer.complete();
                        return;
                    }
                    //Reload the page to ensure all services are cleared.
                    this.router.navigate(['/auth/login']).then(() => {
                        window.location.reload();
                    });
                },
                (err) => {
                    console.error(err);
                },
            );
        });
    }

    /**
     * Change password
     */
    public changePassword(oldPassword: string, newPassword: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            Auth.changePassword(this.authenticatedUser, oldPassword, newPassword).then(
                (success) => {
                    observer.next(success);
                    observer.complete();
                },
                (err) => {
                    observer.error(err);
                },
            );
        });
    }

    public beginValidation(username: string, password: string): void {
        this.userToValidate = { username, password };
    }
    public getUserToValidate(): { username: string; password: string } | undefined {
        return this.userToValidate;
    }
    public endValidation() {
        this.userToValidate = undefined;
    }
}
